import React, {
  useMemo,
  useCallback,
  useRef,
  useContext,
  useEffect,
} from 'react';
import classNames from 'classnames';
import credentialsService from 'services/credentialsService';
import { COOKIE_KEYS } from 'services/cookieService';
import HelmetWrapper from 'components/HelmetWrapper';
import { getResolveUrl } from 'utils';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import InputField from 'components/InputField';
import validations from 'validations';
import AppStoreImg from 'assets/images/AppStoreLink.webp';
import GPlayLink from 'assets/images/GPlayLink.webp';
import QRCode from 'qrcode';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import AuthContext from 'providers/Auth/Auth.context';
import { NEW_WINDOW_PROPS } from 'data/global';

import './TwoFactorAuthentication.styles.scss';
import './TwoFactorAuthentication.styles.responsive.scss';

const GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_AU&pli=1';

const APPLE_STORE_LINK =
  'https://apps.apple.com/au/app/google-authenticator/id388497605';

type TwoFactorAuthenticationProps = {
  className?: string;
};

export const OtpQrCode = ({ otpAuthUrl }: { otpAuthUrl: string }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (otpAuthUrl) {
      QRCode.toCanvas(canvasRef.current, otpAuthUrl, function (error: any) {
        if (error) return;
      });
    }
  }, [otpAuthUrl]);

  return <canvas ref={canvasRef} />;
};

const TwoFactorAuthentication: React.FC<TwoFactorAuthenticationProps> = (
  props,
) => {
  const { className } = props;
  const { t } = useTranslation();
  const { popServerError } = useContext(PopNotificationsContext);
  const { onLoginReq } = useContext(AuthContext);

  const helmetUrl = getResolveUrl('/auth/2fa');
  const title = 'Two factor Authentication';

  const [isTfaConfigured, otPath, uid] = useMemo(() => {
    const props = credentialsService.twoFaProps;

    return [
      props[COOKIE_KEYS.IS_CONF_TFA],
      props[COOKIE_KEYS.OT_PATH],
      props[COOKIE_KEYS.UID],
    ];
  }, []);

  const classes = classNames('two-factor-authentication', className);

  const onSubmit = useCallback(
    async ({ otpToken }: { otpToken: string }) => {
      try {
        const { data: loginResponse } = await api.auth.validateOtpt({
          otpToken,
          userId: uid,
        });
        onLoginReq(loginResponse);
      } catch (e) {
        popServerError(e);
      }
    },
    [onLoginReq, popServerError, uid],
  );

  return (
    <>
      <HelmetWrapper title={title} url={helmetUrl} />

      <h1>{t(isTfaConfigured ? 'twoFA' : 'setupTwoFA')}</h1>

      {!isTfaConfigured && (
        <div className="two-factor-authentication__not-set-cont">
          <div className="two-factor-authentication__not-set-cont__section">
            <div className="text">
              <p>{t('step1')}</p>
              <p>{t('downloadGAA')}</p>
            </div>
            <div className="links">
              <a href={APPLE_STORE_LINK} {...NEW_WINDOW_PROPS}>
                <img src={AppStoreImg} alt="App store link" />
              </a>
              <a href={GOOGLE_PLAY_LINK} {...NEW_WINDOW_PROPS}>
                <img src={GPlayLink} alt="Google play link" />
              </a>
            </div>
          </div>
          <div className="two-factor-authentication__not-set-cont__section">
            <div className="text">
              <p>{t('step2')}</p>
              <p>{t('scanQRCode')}</p>
            </div>
            <div className="code">
              <OtpQrCode otpAuthUrl={otPath} />
            </div>
          </div>

          <div className="two-factor-authentication__not-set-cont__section">
            <div className="text">
              <p>{t('step3')}</p>
              <p>{t('enterTheCode')}</p>
            </div>
          </div>
        </div>
      )}

      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting, valid }) => (
          <form className={classes} onSubmit={handleSubmit}>
            <Field
              placeholder={t('089 332')}
              label={t('6digitCode')}
              name="otpToken"
              autoFocus
              required
              component={InputField}
              validate={validations.required()}
            />
            <Button
              type="submit"
              disabled={!valid || submitting}
              data-loading={submitting}
            >
              {t('signIn')}
            </Button>
          </form>
        )}
      />
    </>
  );
};

export default TwoFactorAuthentication;
